import * as THREE from 'three';

const holdDragInitComponent = {
  schema: {
    cameraId: { default: 'camera' },
    groundId: { default: 'ground' },
    dragDelay: { default: 300 },
    riseHeight: { default: 1 },
  },

  init() {
    // Get camera element
    this.camera = document.getElementById(this.data.cameraId);
    if (!this.camera) {
      throw new Error(
        `[xrextras-hold-drag] Couldn't find camera with id '${this.data.cameraId}'`
      );
    }

    // Get ground element and camera's 3D object
    this.threeCamera = this.camera.getObject3D('camera');
    this.ground = document.getElementById(this.data.groundId);
    if (!this.ground) {
      throw new Error(
        `[xrextras-hold-drag] Couldn't find ground with id '${this.data.groundId}'`
      );
    }

    // Initialize internal state
    this.internalState = {
      fingerDown: false,
      dragging: false,
      distance: 0,
      startDragTimeout: null,
      positionRaw: null,
      raycaster: new THREE.Raycaster(),
    };

    // Bind event handlers
    this.fingerDown = this.fingerDown.bind(this);
    this.startDrag = this.startDrag.bind(this);
    this.fingerMove = this.fingerMove.bind(this);
    this.fingerUp = this.fingerUp.bind(this);

    // Add event listeners
    this.el.addEventListener('mousedown', this.fingerDown);
    this.el.sceneEl.addEventListener('onefingermove', this.fingerMove);
    this.el.sceneEl.addEventListener('onefingerend', this.fingerUp);
    this.el.classList.add('cantap');
  },

  tick() {
    if (!this.internalState.dragging) return;

    let targetPosition = null;

    if (this.internalState.positionRaw) {
      // Normalize screen coordinates to NDC
      const x =
        (this.internalState.positionRaw.x / document.body.clientWidth) * 2 - 1;
      const y =
        (this.internalState.positionRaw.y / document.body.clientHeight) * 2 - 1;
      const ndc = new THREE.Vector2(x, -y);

      // Ensure camera is available
      this.threeCamera = this.threeCamera || this.camera.getObject3D('camera');

      // Cast ray from camera through touch point
      this.internalState.raycaster.setFromCamera(ndc, this.threeCamera);
      const intersects = this.internalState.raycaster.intersectObject(
        this.ground.object3D,
        true
      );

      if (intersects.length > 0) {
        const intersection = intersects[0];
        this.internalState.distance = intersection.distance;
        targetPosition = intersection.point;
      }
    }

    // Fallback position if no intersection
    if (!targetPosition) {
      targetPosition = this.camera.object3D.localToWorld(
        new THREE.Vector3(0, 0, -this.internalState.distance)
      );
    }

    // Apply rise height and smooth interpolation
    targetPosition.y = this.data.riseHeight;
    this.el.object3D.position.lerp(targetPosition, 0.2);
  },

  remove() {
    // Clean up event listeners
    this.el.removeEventListener('mousedown', this.fingerDown);
    this.el.sceneEl.removeEventListener('onefingermove', this.fingerMove);
    this.el.sceneEl.removeEventListener('onefingerend', this.fingerUp);

    // Clean up any active drag
    if (this.internalState.fingerDown) {
      this.fingerUp();
    }
  },

  fingerDown(event) {
    this.internalState.fingerDown = true;
    this.internalState.positionRaw = event.detail.positionRaw;

    // Start drag timeout
    this.internalState.startDragTimeout = setTimeout(
      this.startDrag,
      this.data.dragDelay
    );
  },

  startDrag() {
    if (this.internalState.fingerDown) {
      this.internalState.dragging = true;
      // Calculate initial distance to camera
      this.internalState.distance = this.el.object3D.position.distanceTo(
        this.camera.object3D.position
      );
    }
  },

  fingerMove(event) {
    this.internalState.positionRaw = event.detail.positionRaw;
  },

  fingerUp() {
    this.internalState.fingerDown = false;
    clearTimeout(this.internalState.startDragTimeout);
    this.internalState.positionRaw = null;

    if (this.internalState.dragging) {
      // Animate drop to ground level
      const finalPos = this.el.object3D.position.clone();
      this.el.setAttribute('animation__drop', {
        property: 'position',
        to: `${finalPos.x} 0 ${finalPos.z}`,
        dur: 300,
        easing: 'easeOutQuad',
      });
    }

    this.internalState.dragging = false;
  },
};

export { holdDragInitComponent };
